import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet, apiPost } from "../utils/api";

const DashboardView = ({ email, setMessage }) => {
  const [rssFeedUrl, setRssFeedUrl] = useState("");
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [showProcessingModal, setShowProcessingModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchFeeds();
  }, []);

  const fetchFeeds = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiGet('/getPodcastFeeds');
      setFeeds(response.feeds || []);
    } catch (err) {
      console.error("Error fetching feeds:", err);
      setError("Failed to load podcasts. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Add Podcast Feed
  const handleAddPodcastFeed = async () => {
    if (!rssFeedUrl.trim()) {
      setError("RSS Feed URL cannot be empty.");
      return;
    }

    try {
      setIsAdding(true);
      setError(null);

      // userId is now extracted from authenticated session by Lambda
      const response = await apiPost('/addPodcastFeed', {
        rssFeedUrl
      });

      // Show processing modal
      setShowProcessingModal(true);
      setIsAdding(false);

      setMessage(response.message || "Feed added successfully!");
      window.dispatchEvent(new Event('feeds-updated'));
      setRssFeedUrl("");
      fetchFeeds(); // Refresh the feeds list
    } catch (err) {
      console.error("Error adding feed:", err);
      setError("Error adding feed. Please check the URL and try again.");
      setIsAdding(false);
    }
  };

  const handleAutoProcessingToggle = async (feedId, enabled) => {
    try {
      setError(null);

      // Make API call to update feed preference
      await apiPost('/updateFeedPreference', {
        feedId,
        autoProcessingEnabled: enabled
      });

      // Update local state to reflect the change
      setFeeds(prevFeeds =>
        prevFeeds.map(feed =>
          feed.feedId === feedId
            ? { ...feed, autoProcessingEnabled: enabled }
            : feed
        )
      );

      setMessage(enabled
        ? "Automatic processing enabled for feed."
        : "Automatic processing disabled for feed."
      );
    } catch (err) {
      console.error("Error updating feed preference:", err);
      setError("Failed to update automatic processing preference. Please try again.");
    }
  };

  const handleModalClose = () => {
    setShowProcessingModal(false);
  };

  return (
    <div className="dashboard-content" id="dashboardContent">
      <h2 className="dashboard-heading" id="dashboardHeading">Dashboard</h2>
      
      {error && (
        <div className="dashboard-error-alert" id="dashboardErrorAlert">
          {error}
        </div>
      )}
      
      {/* Add Feed Section */}
      <div className="dashboard-add-feed-card" id="addFeedCard">
        <h3 className="add-feed-heading">Add New Podcast Feed</h3>
        
        <div className="add-feed-form" id="addFeedForm">
          <input
            type="text"
            placeholder="Enter RSS Feed URL"
            value={rssFeedUrl}
            onChange={(e) => setRssFeedUrl(e.target.value)}
            className="add-feed-input"
            id="rssFeedUrlInput"
            disabled={isAdding}
          />
          
          <button 
            onClick={handleAddPodcastFeed}
            className={`add-feed-button ${isAdding ? 'add-feed-button-loading' : ''}`}
            id="addFeedButton"
            disabled={isAdding}
          >
            {isAdding ? "Adding..." : "Add Feed"}
          </button>
        </div>
        
        <p className="add-feed-help-text">
          Enter the RSS feed URL of any podcast to add it to your collection.
        </p>
      </div>
      
      {/* Feeds List */}
      <div className="dashboard-feeds-section" id="feedsSection">
        <h3 className="feeds-heading">Your Podcasts</h3>
        
        {loading ? (
          <p className="feeds-loading-text">Loading your podcasts...</p>
        ) : feeds.length === 0 ? (
          <div className="no-feeds-message" id="noFeedsMessage">
            <p className="no-feeds-primary-text">You haven't added any podcasts yet.</p>
            <p className="no-feeds-secondary-text">Add your first podcast using the form above!</p>
          </div>
        ) : (
          <div className="feeds-grid" id="feedsGrid">
            {feeds.map(feed => (
              <div 
                key={feed.feedId}
                className="feed-card"
                id={`feed-${feed.feedId}`}
                onClick={() => navigate(`/podcast/${feed.feedId}`)}
              >
                <div className="feed-card-content">
                  <div className="feed-card-header">
                    <h4 className="feed-name">
                      {feed.feedName || "Unnamed Podcast"}
                    </h4>
                    
                    {/* Auto-processing toggle */}
                    <div className="feed-auto-process" onClick={(e) => e.stopPropagation()}>
                      <span className="auto-process-label">Auto-process</span>
                      <div className="toggle-switch-container">
                        <input
                          type="checkbox"
                          id={`autoProcessing-${feed.feedId}`}
                          className="toggle-switch-input"
                          checked={feed.autoProcessingEnabled !== false} // Default to true if undefined
                          onChange={(e) => handleAutoProcessingToggle(feed.feedId, e.target.checked)}
                          onClick={(e) => e.stopPropagation()} // Prevent click from navigating
                        />
                        <label
                          htmlFor={`autoProcessing-${feed.feedId}`}
                          className={`toggle-switch ${
                            feed.autoProcessingEnabled !== false ? 'toggle-switch-on' : 'toggle-switch-off'
                          }`}
                          onClick={(e) => e.stopPropagation()} // Prevent click from navigating
                        >
                          <span
                            className={`toggle-switch-slider ${
                              feed.autoProcessingEnabled !== false ? 'toggle-switch-slider-on' : 'toggle-switch-slider-off'
                            }`}
                          ></span>
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  <p className="feed-url">
                    {feed.rssFeedUrl}
                  </p>
                  
                  <div className="feed-card-footer">
                    View Episodes →
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Processing Modal */}
      {showProcessingModal && (
        <div className="feed-processing-modal-overlay">
          <div className="feed-processing-modal">
            <div className="feed-processing-modal-icon">🎉</div>
            <h2 className="feed-processing-modal-title">Feed Processing Started!</h2>
            <p className="feed-processing-modal-message">
              Your podcast feed has been added and is now being processed. We'll notify you when your episodes are ready to view.
            </p>
            <div className="feed-processing-modal-spinner"></div>
            <button
              onClick={handleModalClose}
              className="feed-processing-modal-close"
            >
              Got It!
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardView;